import React, { useState, useContext } from 'react'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import { useNewTranslation } from 'hooks/index'
import { usePermission, useCheckEditCampaignPermission } from 'hooks/permissions'
import { useSelector, useDispatch } from 'react-redux'
import {
    CAMPAIGN_STATUS_ENUM,
    sendCampaignToEdit,
    selectEditingCampaignUuid,
    sendCampaignToPaymentRequest,
    sendCampaignBackToReview,
    CampaignState,
} from 'state/campaigns'
import PopoverButton from 'components/Shared/Button/PopoverButton'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import RectangleButton from 'particles/Button/RectangleButton'
import MenuItem from 'particles/MenuItem'
import Modal from 'particles/Modal'
import { useLocation } from 'react-router-dom'

interface SaveAndSubmitButtonGroupProps {
    campaign?: CampaignState
    disableSubmit?: boolean
    disableSave?: boolean
    onSave: (e: React.MouseEvent<HTMLButtonElement>) => void
    onSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const SaveAndSubmitButtonGroup: React.FC<SaveAndSubmitButtonGroupProps> = ({
    campaign,
    disableSubmit,
    disableSave,
    onSubmit,
    onSave,
}) => {
    const [t] = useNewTranslation()
    const viewType = useContext(ViewTypeContext)
    const location = useLocation()

    const [showToReviewModal, setShowToReviewModal] = useState(false)
    const campaignStatus = campaign?.status
    const hasBudget = campaign?.has_budget
    const isAbleToLaunch =
        (campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaignStatus === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaignStatus === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)
    const canReviewCampaign =
        campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW && usePermission(CampaignStatusControl.ReviewCampaign)
    const isAbleToEdit = (() => {
        if (campaignStatus === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT) {
            if (!location.pathname.includes('payment') && !location.pathname.includes('registration')) {
                return isAbleToLaunch
            }

            return false
        }

        if (campaignStatus === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) {
            if (location.pathname.includes('payment')) {
                return false
            }
            return isAbleToLaunch
        }

        return useCheckEditCampaignPermission(campaignStatus, true, true) && !location.pathname.includes('payment')
    })()

    const ableToRequestPayment = isAbleToLaunch && campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW && hasBudget
    const ableToSendForReview = isAbleToLaunch && campaignStatus === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT
    const editingCampaignUuid = useSelector(selectEditingCampaignUuid)
    const dispatch = useDispatch()

    if (viewType === EDITOR_ROUTE_ENUM.VIEW || campaignStatus === CAMPAIGN_STATUS_ENUM.CLOSED) {
        return null
    }

    const popoverActions = [
        {
            itemPrimaryLabel: t('button.launchCampaign'),
            onClick(e: any) {
                onSubmit?.(e)
            },
            show: isAbleToLaunch,
            disabled: disableSubmit,
        },
        {
            itemPrimaryLabel: t('button.sendPaymentRequest'),
            onClick() {
                editingCampaignUuid && dispatch(sendCampaignToPaymentRequest({ data: { uuid: editingCampaignUuid } }))
            },
            show: ableToRequestPayment,
            disabled: false,
        },
        {
            itemPrimaryLabel: t('button.sendBackToReview'),
            onClick() {
                setShowToReviewModal(true)
            },
            show: ableToSendForReview,
            disabled: false,
        },
        {
            itemPrimaryLabel: t('button.sendToEdit'),
            onClick() {
                editingCampaignUuid && dispatch(sendCampaignToEdit({ data: { uuid: editingCampaignUuid } }))
            },
            show: canReviewCampaign,
        },
    ]

    const handleSendToReview = () => {
        setShowToReviewModal(false)
        editingCampaignUuid && dispatch(sendCampaignBackToReview({ data: { uuid: editingCampaignUuid } }))
    }

    return (
        <div className="button-group">
            {campaignStatus !== undefined &&
            [
                CAMPAIGN_STATUS_ENUM.DRAFT,
                CAMPAIGN_STATUS_ENUM.FOR_REVIEW,
                CAMPAIGN_STATUS_ENUM.FOR_EDIT,
                CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT,
                CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH,
            ].includes(campaignStatus) ? (
                <>
                    {campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
                    campaignStatus === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
                    campaignStatus === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH ? (
                        isAbleToLaunch || canReviewCampaign ? (
                            <PopoverButton
                                popoverTitleText={t('campaignEditor.selectActionMenuHeader')}
                                buttonProps={{
                                    label: t('button.selectAction'),
                                    icon: 'arrowUpRight',
                                }}
                            >
                                {(closePopup: () => void) => (
                                    <>
                                        <MenuItem.Title
                                            text={t('campaignEditor.selectActionMenuHeader')}
                                            case="uppercase"
                                        />
                                        {popoverActions?.map(
                                            (option, index) =>
                                                option.show && (
                                                    <MenuItem.Text
                                                        key={`popoverButton-${index}`}
                                                        text={option.itemPrimaryLabel}
                                                        onClick={e => {
                                                            option.onClick(e)
                                                            closePopup()
                                                        }}
                                                        isDisabled={option.disabled}
                                                        tabIndex={index}
                                                    />
                                                ),
                                        )}
                                    </>
                                )}
                            </PopoverButton>
                        ) : null
                    ) : (
                        <RectangleButton
                            form="outlined"
                            size="m"
                            label={t('button.sendToReview')}
                            data-testid="test-campaign-move-to-review"
                            isDisabled={disableSubmit}
                            onClick={onSubmit}
                        />
                    )}
                </>
            ) : null}
            {isAbleToEdit ? (
                <RectangleButton
                    form="filled"
                    data-testid="test-campaign-save"
                    size="m"
                    label={t('button.saveChanges')}
                    isDisabled={disableSave}
                    onClick={onSave}
                />
            ) : null}
            {showToReviewModal && (
                <Modal
                    title={t('modalWindow.sendBackToReviewTitle')}
                    subtext={t('modalWindow.sendBackToReviwSubtext')}
                    size={'s'}
                    isClosable
                    hasButtonSet
                    hasIcon={false}
                    onConfirm={handleSendToReview}
                    onClose={() => setShowToReviewModal(false)}
                    primaryButtonProps={{
                        label: t('button.sendBackToReview'),
                    }}
                    secondaryButtonProps={{
                        label: t('helper.uploadProgress.inProgress.button.cta1'),
                    }}
                />
            )}
        </div>
    )
}

export default SaveAndSubmitButtonGroup
